import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { checkOverflow } from '../../utils/ids-dom-utils/ids-dom-utils';
import IdsTooltipMixin from '../../mixins/ids-tooltip-mixin/ids-tooltip-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsColorVariantMixin from '../../mixins/ids-color-variant-mixin/ids-color-variant-mixin';
import IdsHideFocusMixin from '../../mixins/ids-hide-focus-mixin/ids-hide-focus-mixin';
import IdsHitboxMixin from '../../mixins/ids-hitbox-mixin/ids-hitbox-mixin';
import IdsElement from '../../core/ids-element';

import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';

import styles from './ids-hyperlink.scss';

const Base = IdsHideFocusMixin(
  IdsHitboxMixin(
    IdsColorVariantMixin(
      IdsTooltipMixin(
        IdsEventsMixin(
          IdsElement
        )
      )
    )
  )
);

/**
 * IDS Hyperlink Component
 * @type {IdsHyperlink}
 * @inherits IdsElement
 * @mixes IdsHitboxMixin
 * @mixes IdsHideFocusMixin
 * @mixes IdsColorVariantMixin
 * @mixes IdsEventsMixin
 * @part link - the link element
 */
@customElement('ids-hyperlink')
@scss(styles)
export default class IdsHyperlink extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    if (!(this.getAttribute('role'))) this.setAttribute('role', 'link');

    this.#attachEventHandlers();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.ALLOW_EMPTY_HREF,
      attributes.CLICKABLE,
      attributes.COLOR,
      attributes.COL_SPAN,
      attributes.DISABLED,
      attributes.FONT_SIZE,
      attributes.FONT_WEIGHT,
      attributes.HREF,
      attributes.TARGET,
      attributes.TEXT_DECORATION,
      attributes.TEXT_ELLIPSIS,
      attributes.SHOW_VISITED_COLOR,
      attributes.OVERFLOW
    ];
  }

  /**
   * Attaches event handlers
   * @private
   * @returns {void}
   */
  #attachEventHandlers(): void {
    this.onEvent('click.ids-hyperlink', this, (e: Event) => {
      if (this.disabled) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    });
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    const disabledAttr = this.disabled ? ' disabled' : '';
    const hrefAttr = this.href ? ` href="${this.href}"` : '';
    const tabIndexAttr = this.disabled ? ' tabindex="-1"' : '';
    const targetAttr = this.target ? ` target="${this.target}"` : '';

    const colorClass = this.color === 'unset' ? ' ids-hyperlink-color-unset' : '';
    const fontSizeClass = this.fontSize ? ` ids-text-${this.fontSize}` : '';
    const fontWeightClass = this.fontWeight ? ` ${this.fontWeight}` : '';
    const textDecorationClass = this.textDecoration ? ` ids-text-decoration-${this.textDecoration}"` : '';
    const textEllipsisClass = this.textEllipsis ? ` text-ellipsis` : '';

    return `<a
      class="ids-hyperlink ${textEllipsisClass}${colorClass}${fontSizeClass}${fontWeightClass}${textDecorationClass}"
      ${disabledAttr}
      ${hrefAttr}
      ${tabIndexAttr}
      ${targetAttr}
      part="link">
        <slot></slot>
    </a>`;
  }

  /**
   *
   */
  colorVariants = ['breadcrumb', 'alternate'];

  /**
   * Set the text to be clickable
   * @param {boolean | string} value True if clickable
   */
  set clickable(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.toggleAttribute(attributes.CLICKABLE, true);
      this.tabIndex = 1;
      this.container?.classList.add('clickable');
    } else {
      this.tabIndex = 0;
      this.removeAttribute(attributes.CLICKABLE);
      this.container?.classList.remove('clickable');
    }
  }

  get clickable(): boolean { return stringToBool(this.getAttribute(attributes.CLICKABLE)); }

  /**
   * Set the link href
   * @param {string} value Set the link's href to some link
   */
  set href(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HREF, value);
      this.container?.setAttribute(attributes.HREF, value);
      return;
    }
    this.removeAttribute(attributes.HREF);
    this.container?.removeAttribute(attributes.HREF);
  }

  get href(): string | null {
    return this.getAttribute(attributes.HREF);
  }

  /**
   * Set the link target attribute
   * @param {string} value Set the link's href to some link
   */
  set target(value: string | null) {
    if (value) {
      this.setAttribute(attributes.TARGET, value);
      this.container?.setAttribute(attributes.TARGET, value);
      return;
    }
    this.removeAttribute(attributes.TARGET);
    this.container?.removeAttribute(attributes.TARGET);
  }

  get target(): string | null {
    return this.getAttribute(attributes.TARGET);
  }

  /**
   * Set the link text decoration styling
   * @param {string} value If 'none', removes text decoration, If hover then just on hover it
   * is shown.
   */
  set textDecoration(value: string | null) {
    this.container?.classList.remove('ids-text-decoration-none', 'ids-text-decoration-hover');
    if (value?.toLowerCase() === 'none') {
      this.setAttribute(attributes.TEXT_DECORATION, value);
      this.container?.classList.add('ids-text-decoration-none');
      return;
    }
    if (value?.toLowerCase() === 'hover') {
      this.setAttribute(attributes.TEXT_DECORATION, value);
      this.container?.classList.add('ids-text-decoration-hover');
      return;
    }
    this.removeAttribute(attributes.TEXT_DECORATION);
  }

  get textDecoration(): string | null {
    return this.getAttribute(attributes.TEXT_DECORATION);
  }

  /**
   * Show/Hide the visited color on the link
   * @param {boolean} value if true visited color is shown
   */
  set showVisitedColor(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.SHOW_VISITED_COLOR, '');
      this.container?.classList.add('show-visited-color');
      this.container?.setAttribute('tabindex', '-1');
      return;
    }
    this.removeAttribute(attributes.SHOW_VISITED_COLOR);
    this.container?.classList.add('show-visited-color');
  }

  get showVisitedColor(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_VISITED_COLOR)) || false;
  }

  /**
   * Set the text to disabled color.
   * @param {boolean | string} value True if disabled
   */
  set disabled(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.DISABLED, '');
      this.container?.setAttribute(attributes.DISABLED, '');
      this.container?.setAttribute('tabindex', '-1');
      return;
    }
    this.removeAttribute(attributes.DISABLED);
    this.container?.removeAttribute(attributes.DISABLED);
    this.container?.removeAttribute('tabindex');
  }

  get disabled(): boolean | string {
    return this.hasAttribute(attributes.DISABLED);
  }

  /**
   *
   * If set to "unset", color can be controlled by parent container
   * @param {string | null} value  "unset" or undefined/null
   */
  set color(value: string | null) {
    if (value === 'unset') {
      this.setAttribute(attributes.COLOR, value);
      this.container?.classList.add('ids-hyperlink-color-unset');
    } else {
      this.removeAttribute(attributes.COLOR);
      this.container?.classList.remove('ids-hyperlink-color-unset');
    }
  }

  get color(): string | null {
    return this.getAttribute(attributes.COLOR);
  }

  fontSizes = [
    { 10: '10px' },
    { 12: '12px' },
    { 14: '14px' },
    { 16: '16px' },
    { 20: '20px' },
    { 24: '24px' },
    { 28: '28px' },
    { 32: '32px' },
    { 40: '40px' },
    { 48: '48px' },
    { 60: '60px' },
    { 72: '72px' }
  ];

  /**
   * Set the font size/style of the text with a class.
   * @param {string | null} value The font size in the font scheme
   * i.e. 10, 12, 16 or xs, sm, base, lg, xl
   */
  set fontSize(value: string | null) {
    this.fontSizes?.forEach((string: any) => {
      this.container?.classList.remove(`ids-text-${Object.keys(string)}`);
    });

    if (value) {
      this.setAttribute(attributes.FONT_SIZE, value);
      this.container?.classList.add(`ids-text-${value}`);
      return;
    }

    this.removeAttribute(attributes.FONT_SIZE);
  }

  get fontSize(): string | null { return this.getAttribute(attributes.FONT_SIZE); }

  /**
   * Adjust font weight; can be either "bold" "lighter" or not present
   * since font supports 300, 400, 600
   * @param {string | null} value (if bold)
   */
  set fontWeight(value: string | null) {
    this.container?.classList.remove('bold', 'lighter', 'semibold');

    if (value === 'semibold' || value === 'bold' || value === 'lighter') {
      this.setAttribute(attributes.FONT_WEIGHT, value);
      this.container?.classList.add(value);
      return;
    }

    this.removeAttribute(attributes.FONT_WEIGHT);
  }

  get fontWeight(): string | null {
    return this.getAttribute(attributes.FONT_WEIGHT);
  }

  /**
   * Allows underline and styling of the link when href attribute is empty
   * @param {boolean | string} value whether or not to allow underline when href attribute is empty
   */
  set allowEmptyHref(value: boolean | string) {
    const boolVal = stringToBool(value);
    this.setAttribute(attributes.ALLOW_EMPTY_HREF, String(boolVal));
  }

  get allowEmptyHref(): boolean | string {
    const attrVal = this.getAttribute(attributes.ALLOW_EMPTY_HREF);
    if (attrVal) {
      return stringToBool(attrVal);
    }

    return true;
  }

  /**
   * Set the col-span attribute
   * @param {string | null} value If 2 will span 2 columns, nothing else is valid
   */
  set colSpan(value: string | null) {
    if (value !== null) {
      this.setAttribute(attributes.COL_SPAN, value);
      this.container?.classList.add('colspan');
    } else {
      this.removeAttribute(attributes.COL_SPAN);
      this.container?.classList.remove('colspan');
    }
  }

  /**
   * Get col-span attribute
   * @returns {string | null} The number value for the columns to span in the grid
   */
  get colSpan(): string | null {
    return this.getAttribute(attributes.COL_SPAN);
  }

  /**
   * Set how content overflows; can specify 'ellipsis', or undefined or 'none'
   * @param {string | null} value how content is overflow
   */
  set overflow(value: string | null) {
    const isEllipsis = value === 'ellipsis';

    if (isEllipsis) {
      this.container?.classList.add('ellipsis');
      this.setAttribute('overflow', 'ellipsis');
    } else {
      this.container?.classList.remove('ellipsis');
      this.removeAttribute('overflow');
    }
  }

  get overflow(): string | null {
    return this.getAttribute('overflow');
  }

  /**
   * Set text-ellipsis attribute
   * @param {boolean | string} value The value
   */
  set textEllipsis(value: boolean | string) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.TEXT_ELLIPSIS, '');
      this.container?.classList.add('text-ellipsis');
    } else {
      this.removeAttribute(attributes.TEXT_ELLIPSIS);
      this.container?.classList.remove('text-ellipsis');
    }
  }

  /**
   * Get text-ellipsis attribute
   * @returns {boolean} True if text should be truncated with ellipsis
   */
  get textEllipsis(): boolean | string {
    return this.hasAttribute(attributes.TEXT_ELLIPSIS);
  }

  canTooltipShow() {
    if (this.tooltip && this.overflow === null && this.overflow !== 'ellipsis') {
      return true;
    }
    if (this.tooltip && this.tooltip !== 'false' && this.container) {
      return ((checkOverflow(this.container) || checkOverflow(this.parentElement))) || false;
    }

    return false;
  }
}
